import { useState, type FormEvent } from 'react';
import { Check, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
    exactRecipeAmount,
    formatItemAmount,
    friendlyAmount,
    parseAmountInput,
    unitForEdit,
} from '@/lib/shoppingItems';
import type { ShoppingListItem, UpdateShoppingItemPayload } from '@/services/shoppingLists';

interface ShoppingItemRowProps {
    item: ShoppingListItem;
    editing: boolean;
    /** Another write (save or remove) on this list is in flight. */
    busy: boolean;
    onTick: (item: ShoppingListItem) => void;
    onEdit: (item: ShoppingListItem) => void;
    onCancel: () => void;
    onSave: (item: ShoppingListItem, change: UpdateShoppingItemPayload) => void;
    onRemove: (item: ShoppingListItem) => void;
}

/**
 * One line on the list: a big tick box, the amount in bold and the name. The
 * pencil opens an inline editor (amount as free text, plus the name on a
 * line the client added) with Save, Cancel and Remove.
 */
export function ShoppingItemRow({
    item,
    editing,
    busy,
    onTick,
    onEdit,
    onCancel,
    onSave,
    onRemove,
}: ShoppingItemRowProps) {
    const { main: amountText, used } = friendlyAmount(item);
    const freeText = !item.ingredientId;
    const label = [
        amountText ? `${amountText} ${item.displayName}` : item.displayName,
        used ? `, ${used} used` : '',
    ].join('');

    const tickBox = (
        <span
            aria-hidden="true"
            className={cn(
                'flex size-8 shrink-0 items-center justify-center rounded-lg border-2 transition-colors',
                item.isPurchased
                    ? 'border-brand-text-green bg-brand-text-green text-white'
                    : 'border-brand-header bg-white',
            )}
        >
            {item.isPurchased && <Check className="size-5" strokeWidth={3} />}
        </span>
    );

    if (editing) {
        return (
            <li className="border-l-4 border-brand-header bg-brand-header-wash px-4 py-3">
                <ItemEditor
                    item={item}
                    tickBox={
                        <button
                            type="button"
                            role="checkbox"
                            aria-checked={item.isPurchased}
                            aria-label={label}
                            onClick={() => onTick(item)}
                            className="rounded-lg focus-visible:outline-2 focus-visible:outline-brand-header"
                        >
                            {tickBox}
                        </button>
                    }
                    busy={busy}
                    onCancel={onCancel}
                    onSave={(change) => onSave(item, change)}
                    onRemove={() => onRemove(item)}
                />
            </li>
        );
    }

    return (
        <li
            className={cn(
                'flex items-center gap-2 pr-2',
                item.isPurchased ? 'bg-muted' : 'bg-brand-surface',
            )}
        >
            <button
                type="button"
                role="checkbox"
                aria-checked={item.isPurchased}
                aria-label={label}
                onClick={() => onTick(item)}
                className="flex min-h-14 min-w-0 flex-1 items-center gap-3 py-2.5 pl-4 text-left focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-header"
            >
                {tickBox}
                <span
                    className={cn(
                        'min-w-0 break-words text-base',
                        item.isPurchased ? 'text-muted-foreground line-through' : 'text-foreground',
                    )}
                >
                    {amountText && <strong className="font-bold">{amountText}</strong>}{' '}
                    {item.displayName}
                    {used && (
                        <span className="block text-sm text-muted-foreground">{used} used</span>
                    )}
                </span>
            </button>
            <button
                type="button"
                aria-label={`Edit ${item.displayName}`}
                onClick={() => onEdit(item)}
                className="flex size-10 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-brand-header-wash hover:text-brand-text-blue"
            >
                <Pencil className="size-4" />
            </button>
            {freeText && (
                <button
                    type="button"
                    aria-label={`Remove ${item.displayName}`}
                    disabled={busy}
                    onClick={() => onRemove(item)}
                    className="flex size-10 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
                >
                    <Trash2 className="size-4" />
                </button>
            )}
        </li>
    );
}

interface ItemEditorProps {
    item: ShoppingListItem;
    tickBox: React.ReactNode;
    busy: boolean;
    onCancel: () => void;
    onSave: (change: UpdateShoppingItemPayload) => void;
    onRemove: () => void;
}

function ItemEditor({ item, tickBox, busy, onCancel, onSave, onRemove }: ItemEditorProps) {
    const freeText = !item.ingredientId;
    // Editing starts from the exact stored amount, not the rounded one: that
    // is the number merges add to, and saving it untouched changes nothing.
    const initialAmount = formatItemAmount(item);
    const [amountText, setAmountText] = useState(initialAmount);
    const [name, setName] = useState(item.displayName);
    const [error, setError] = useState<string | null>(null);
    const amountId = `amount-${item.id}`;
    const nameId = `name-${item.id}`;

    const submit = (event: FormEvent) => {
        event.preventDefault();
        const parsed = parseAmountInput(amountText);
        const change: UpdateShoppingItemPayload = {};
        // The box shows at most two decimals, so 2.8333 reads "2.83". Unless
        // the text was changed, the stored amount stays as it is.
        const amountTouched = amountText.trim() !== initialAmount;

        if (freeText) {
            const trimmed = name.trim();
            if (!trimmed) {
                setError('Give the item a name.');
                return;
            }
            if (trimmed !== item.displayName) change.name = trimmed;
        } else if (parsed.amount === null) {
            setError('Start with a number, like 500 g.');
            return;
        }

        // A bare number keeps the row's unit, and typing back the stored unit
        // in another form ("g" for "gram") is not a change, so the row still
        // matches the menu (#376).
        const unit = unitForEdit(parsed, item.unit);
        if (amountTouched && parsed.amount !== item.amount) change.amount = parsed.amount;
        if (amountTouched && unit !== item.unit) change.unit = unit;

        if (Object.keys(change).length === 0) {
            onCancel();
            return;
        }
        setError(null);
        onSave(change);
    };

    const fromRecipes = item.source !== 'MANUAL';
    const recipeAmount = exactRecipeAmount(item);

    return (
        <form onSubmit={submit} aria-label={`Edit ${item.displayName}`}>
            <div className="flex items-center gap-3">
                {tickBox}
                {freeText ? (
                    <div className="min-w-0 flex-1">
                        <label htmlFor={nameId} className="sr-only">
                            Name
                        </label>
                        <Input
                            id={nameId}
                            value={name}
                            maxLength={200}
                            onChange={(event) => setName(event.target.value)}
                            className="h-10 bg-white text-base"
                        />
                    </div>
                ) : (
                    <span className="min-w-0 break-words text-base text-foreground">
                        {item.displayName}
                    </span>
                )}
            </div>

            <div className="mt-3 sm:pl-11">
                <label htmlFor={amountId} className="mb-1 block text-xs text-muted-foreground">
                    Amount
                </label>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-3">
                    <Input
                        id={amountId}
                        value={amountText}
                        autoFocus={!freeText}
                        maxLength={60}
                        placeholder={freeText ? 'Optional, like 2 packs' : 'Like 500 g'}
                        onChange={(event) => setAmountText(event.target.value)}
                        aria-invalid={!!error}
                        aria-describedby={error ? `${amountId}-error` : undefined}
                        className="h-11 w-28 bg-white text-base sm:w-36"
                    />
                    <Button type="submit" disabled={busy} className="h-11 px-5 font-bold">
                        Save
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={onCancel}
                        className="h-11 text-muted-foreground"
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        disabled={busy}
                        onClick={onRemove}
                        className="ml-auto h-11 text-red-700 hover:bg-red-50 hover:text-red-800"
                    >
                        <Trash2 className="size-4" />
                        Remove
                    </Button>
                </div>
                {error && (
                    <p id={`${amountId}-error`} role="alert" className="mt-2 text-sm text-red-700">
                        {error}
                    </p>
                )}
                {fromRecipes && (
                    <p className="mt-2 text-xs text-muted-foreground">
                        {item.userEdited
                            ? "You set this amount. We won't change it if the list updates."
                            : `Recipes use ${recipeAmount}. We won't change your amount if the list updates.`}
                    </p>
                )}
            </div>
        </form>
    );
}
